import React from "react";
import styles from "./PerksOfTheWeek.module.scss";
import { Entry } from "contentful";
import {
  ContentfulContentType,
  PerkDocument,
  PerkOfTheWeekDocument,
} from "@tiicker/util/lib/contentful/types";
import { useContentfulEntries } from "source/contentful/provider";
import PerkCard from "components/core/PerkCard/PerkCard";
import { getImageReference } from "@tiicker/util/lib/contentful/helpers";
import CurrentPerkOfTheWeek from "./CurrentPerkOfTheWeek";
import classNames from "classnames";
import { Media } from "components/Media/media";
import { useUser } from "components/UserContext/UserContext";
import Container from "components/core/Layout/Container/Container";

type Props = {
  perksOfTheWeek: Entry<PerkOfTheWeekDocument>[];
  perksOfTheWeekIncludes: any;
  eligiblePerkIds: string[];
  claimedPerkIds: { claimId: number; perkId: string }[];
  canClaimPerks: boolean;
};

const PerkTile = (props: {
  perk: Entry<PerkDocument>;
  perksOfTheWeekIncludes: any;
}) => {
  const userContext = useUser();

  const image = getImageReference(
    props.perk.fields.image,
    props.perksOfTheWeekIncludes
  );
  const logo = getImageReference(
    props.perk.fields.brand.fields.companyLogoDark,
    props.perksOfTheWeekIncludes
  );

  return (
    <div
      className={classNames("col-4", styles.previousCard)}
      key={`perk-of-the-week-${props.perk.sys.id}`}
    >
      <PerkCard
        id={props.perk.sys.id}
        title={props.perk.fields.title}
        imageUrl={image?.fields.file.url ?? ""}
        brandLogo={logo?.fields.file.url ?? ""}
        tickerSymbol={props.perk.fields.brand.fields.tickerSymbol}
        cardClassName={styles.perkCard}
        className={styles.fullCard}
        noAnimate
        availableToClaim={userContext.state.availablePerks.some(
          (availablePerk) => availablePerk.perkId === props.perk.sys.id
        )}
        limitedQuantityNumber={props.perk.fields.limitedQuantityNumber}
      />
    </div>
  );
};

const PerksOfTheWeek = (props: Props) => {
  const perkQuery = useContentfulEntries<PerkDocument>(
    ContentfulContentType.Perk,
    {
      query: {
        content_type: ContentfulContentType.Perk,
        "sys.id[in]": props.perksOfTheWeek
          .map((potw) => potw.fields.perk.sys.id)
          .join(","),
      },
    }
  );
  if (perkQuery.state !== "DONE") {
    return <></>;
  }

  const sortedPerks = props.perksOfTheWeek.map(
    (potw) =>
      perkQuery.result.items.filter(
        (p) => p.sys.id === potw.fields.perk.sys.id
      )[0]
  );

  const currentPerkOfTheWeek = sortedPerks[0];
  if (!currentPerkOfTheWeek) {
    return <></>;
  }

  const previousPerksOfTheWeek =
    sortedPerks.length > 1 ? sortedPerks.slice(1) : [];

  const trackerClass = `view-perk-of-the-week-${
    currentPerkOfTheWeek.fields.brand.fields.tickerSymbol
  }-${currentPerkOfTheWeek.fields.title.replace(/ /g, "-")}`;

  return (
    <div className={styles.perkOfTheWeekSection}>
      <div className="container">
        <h3 className={styles.POTWTitle}>Featured Perk</h3>
        <CurrentPerkOfTheWeek
          perk={currentPerkOfTheWeek}
          includes={props.perksOfTheWeekIncludes}
        />
      </div>
      {previousPerksOfTheWeek && previousPerksOfTheWeek.length > 0 && (
        <>
          <div className="container">
            <h3 className={styles.POTWTitle}>Previously Featured Perks</h3>
          </div>
          <Media greaterThanOrEqual="lg">
            <div className="container">
              <div className="row">
                {previousPerksOfTheWeek
                .filter((perk) => new Date(perk.fields.endDate) > new Date())
                .slice(0, 3)
                .map((perk) => (
                  <PerkTile
                    key={perk.sys.id}
                    perk={perk}
                    perksOfTheWeekIncludes={props.perksOfTheWeekIncludes}
                  />
                ))}
              </div>
            </div>
          </Media>
          <Media lessThan="lg">
            <div className={classNames(styles.previousContainer)}>
              <Container>
                <div className={classNames("row", styles.previousRow)}>
                  {previousPerksOfTheWeek
                  .filter((perk) => new Date(perk.fields.endDate) > new Date())
                  .slice(0, 3)
                  .map((perk) => (
                    <PerkTile
                      key={perk.sys.id}
                      perk={perk}
                      perksOfTheWeekIncludes={props.perksOfTheWeekIncludes}
                    />
                  ))}
                </div>
              </Container>
            </div>
          </Media>
        </>
      )}
    </div>
  );
};

export default PerksOfTheWeek;
